import { postApiChatEvaluationTestSetImport, useDeleteApiChatEvaluationTestSetDeleteSamples, useGetApiChatEvaluationTestSetGet, usePostApiChatEvaluationTestSetAddSamples, usePostApiChatEvaluationTestSetUpdateSample } from "@/API"
import { ActionIcon, Box, Button, FileButton, Group, Pagination, Text, Tooltip } from "@mantine/core"
import { modals } from "@mantine/modals"
import { notifications } from "@mantine/notifications"
import { IconEdit, IconFileArrowLeft, IconFileArrowRight, IconFilePlus, IconFlame, IconFlask, IconRefresh, IconTrash } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { useParams } from "@tanstack/react-router"
import { DataTable } from "mantine-datatable"
import { EditTestSetSampleButton } from "./EditTestSetSample"
import { useRef, useState } from "react"
import { GenerateTestSetJobManagerButton } from "./GenerateTestSetJobManager"
import { PageTitle } from "@/Components/PageTitle"
import { UrlSaveToFileAsync } from "@/Helpers/SaveToFile"
import { GetTestSetExportUrl } from "@/APIURL"
import { EvaluateTestButton } from "../Evaluation/EvaluateTest"

export function ChatEvaluationTestSetPage() {
    const { workspaceId, chatEvaluationTestSetId } = useParams({ from: "/workspace/$workspaceId/chatEvaluationTestSet/$chatEvaluationTestSetId/" })
    const queryClient = useQueryClient()
    const testSet = useGetApiChatEvaluationTestSetGet({ workspaceId: workspaceId, id: chatEvaluationTestSetId })
    const PAGE_SIZE = 5
    const [activePage, setPage] = useState(1);

    const create_Mutation = usePostApiChatEvaluationTestSetAddSamples()
    const update_Mutation = usePostApiChatEvaluationTestSetUpdateSample()
    const delete_Mutation = useDeleteApiChatEvaluationTestSetDeleteSamples()
    const samples = testSet.data?.data.samples ?? []

    const [importLoading, setImportLoading] = useState(false)
    const resetRef = useRef<() => void>(null);

    return <>
        <Group mb="md" gap="sm">
            <PageTitle text="測試資料集" />
            <ActionIcon variant="subtle" onClick={() => {
                queryClient.invalidateQueries({ queryKey: testSet.queryKey })
            }}>
                <IconRefresh style={{ width: '70%', height: '70%' }} />
            </ActionIcon>
            <GenerateTestSetJobManagerButton workspaceId={workspaceId} testSetId={chatEvaluationTestSetId}>
                <Button leftSection={<IconFlame />}>
                    產生工作管理
                </Button>
            </GenerateTestSetJobManagerButton>
            <EditTestSetSampleButton initValue={{
                sampleId: "",
                userInput: "",
                reference: ""
            }} onSubmit={async v => {
                setImportLoading(true)
                try {
                    await create_Mutation.mutateAsync({
                        params: {
                            workspaceId: workspaceId,
                            id: chatEvaluationTestSetId
                        },
                        data: [{
                            ...v,
                            sampleId: ""
                        }]
                    })
                    queryClient.invalidateQueries({ queryKey: testSet.queryKey })
                    notifications.show({ message: "新增成功" })
                } catch (e) {
                    notifications.show({ message: "新增失敗", color: "red" })
                }
                setImportLoading(false)
            }}>
                <Button leftSection={<IconFilePlus />} loading={importLoading}>
                    新增資料
                </Button>
            </EditTestSetSampleButton>
            <Button disabled={samples.length == 0} loading={importLoading} leftSection={<IconFileArrowRight />} onClick={() => {
                setImportLoading(true)
                try {
                    UrlSaveToFileAsync(GetTestSetExportUrl(workspaceId, chatEvaluationTestSetId))
                } catch (e) {
                    notifications.show({ message: "匯出失敗", color: "red" })
                }
                setImportLoading(false)
            }}>
                匯出
            </Button>
            <FileButton resetRef={resetRef} onChange={async f => {
                resetRef.current?.()
                if (!f) return
                setImportLoading(true)
                try {
                    await postApiChatEvaluationTestSetImport({
                        file: f
                    }, { workspaceId: workspaceId, id: chatEvaluationTestSetId })
                    notifications.show({ message: "匯入成功" })
                    queryClient.invalidateQueries({ queryKey: testSet.queryKey })
                } catch (e) {
                    notifications.show({ message: "匯入失敗", color: "red" })
                }
                setImportLoading(false)
            }} accept="text/csv">
                {(props) =>
                    <Button {...props} loading={importLoading} leftSection={<IconFileArrowLeft />}>
                        匯入
                    </Button>
                }
            </FileButton>
        </Group>
        <Pagination value={activePage} onChange={setPage} total={Math.ceil((testSet.data?.data.samples?.length ?? 0) / PAGE_SIZE)} />
        <DataTable
            withTableBorder
            minHeight={samples.length > 0 ? 0 : 150}
            noRecordsText="沒有資料"
            fetching={testSet.isFetching}
            columns={[
                { accessor: 'userInput', title: "使用者輸入" },
                { accessor: 'reference', title: "標準回應" },
                {
                    accessor: 'actions',
                    title: <Box mr={6}>操作</Box>,
                    textAlign: 'right',
                    render: (data) => (
                        <Group gap={4} justify="right" wrap="nowrap">
                            <Tooltip label="即時單一評估測試">
                                <EvaluateTestButton workspaceId={workspaceId} sample={data}>
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                    >
                                        <IconFlask size={16} />
                                    </ActionIcon>
                                </EvaluateTestButton>
                            </Tooltip>
                            <Tooltip label="編輯樣本">
                                <EditTestSetSampleButton initValue={data} onSubmit={async v => {
                                    try {
                                        await update_Mutation.mutateAsync({
                                            params: {
                                                workspaceId: workspaceId,
                                                id: chatEvaluationTestSetId
                                            },
                                            data: v
                                        })
                                        queryClient.invalidateQueries({ queryKey: testSet.queryKey })
                                        notifications.show({ message: "更新成功" })
                                    } catch (e) {
                                        notifications.show({ message: "更新失敗", color: "red" })
                                    }
                                }}>
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                    >
                                        <IconEdit size={16} />
                                    </ActionIcon>
                                </EditTestSetSampleButton>
                            </Tooltip>
                            <Tooltip label="刪除樣本">
                                <ActionIcon
                                    size="sm"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => {
                                        modals.openContextModal({
                                            modal: "ConfirmCountDown",
                                            title: '刪除',
                                            innerProps: {
                                                countDownSecond: 0,
                                                children: (
                                                    <Text size="sm">
                                                        是否要刪除"{data.userInput}"?
                                                    </Text>
                                                ),
                                                labels: {
                                                    confirm: '刪除', cancel: '取消'
                                                },
                                                confirmProps: { color: 'red' },
                                                onConfirm: async () => {
                                                    try {
                                                        if (data.sampleId) {
                                                            await delete_Mutation.mutateAsync({
                                                                params: {
                                                                    workspaceId: workspaceId,
                                                                    id: chatEvaluationTestSetId
                                                                },
                                                                data: [data.sampleId]
                                                            })
                                                            queryClient.invalidateQueries({ queryKey: testSet.queryKey })
                                                            notifications.show({ message: "刪除成功" })
                                                        }
                                                    } catch (e) {
                                                        notifications.show({ message: "刪除失敗", color: "red" })
                                                    }
                                                },
                                            },

                                        });
                                    }}
                                >
                                    <IconTrash size={16} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    ),
                },
            ]}
            records={samples.slice((activePage - 1) * PAGE_SIZE, activePage * PAGE_SIZE).map(sample => {
                return {
                    id: sample.sampleId,
                    ...sample
                }
            })}
        />
    </>
}
